/**
 * Aplicaciones de patrones (008 · T087 · US5; contracts/admin-api.md A12 y A14): quién tiene qué
 * patrón, desde cuándo, con qué desfase y hasta dónde está generado.
 *
 * Filtros en borrador (QA-2): patrón, trabajador y estado (defecto "Activas"), con "Buscar",
 * "Limpiar filtros" y "Actualizar"; paginación por cursor ("Cargar más"). "Terminar" con
 * `schedules.write` sobre una aplicación activa y manual; las de integración van con candado.
 */
import { Lock, Repeat } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { SchedulePattern, WorkerSchedulePattern } from '@fermin/contracts';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../../components/ui/card';
import { Combobox } from '../../../components/ui/combobox';
import { EmptyState } from '../../../components/ui/empty-state';
import { Field } from '../../../components/ui/field';
import { FilterActions, FilterForm } from '../../../components/ui/filter-actions';
import { RefreshButton } from '../../../components/ui/refresh-button';
import { Select, type SelectOption } from '../../../components/ui/select';
import { SkeletonLines } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../../components/ui/table';
import { t } from '../../../i18n';
import { useAppliedFilters } from '../../../lib/use-applied-filters';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import {
  patternApplicationsKey,
  scheduleErrorMessage,
  usePatternApplications,
  useWorkerOptions,
  type PatternApplicationFilters,
} from '../api';
import { formatLocalDay } from '../format';

import { EndApplicationDialog } from './end-application-dialog';

const ALL = 'ALL';
type ApplicationStatus = 'ACTIVE' | 'ENDED' | 'ALL';
const STATUSES: readonly ApplicationStatus[] = ['ACTIVE', 'ENDED', 'ALL'];

interface ApplicationDraft {
  pattern_id: string;
  worker_id: string;
  status: ApplicationStatus;
}

const DEFAULT_DRAFT: ApplicationDraft = { pattern_id: ALL, worker_id: '', status: 'ACTIVE' };

export function applicationFiltersFrom(draft: ApplicationDraft): PatternApplicationFilters {
  return {
    ...(draft.pattern_id !== ALL ? { pattern_id: draft.pattern_id } : {}),
    ...(draft.worker_id ? { worker_id: draft.worker_id } : {}),
    status: draft.status,
  };
}

export interface ApplicationsSectionProps {
  patterns: readonly SchedulePattern[];
  canWrite: boolean;
  today: string;
}

export function ApplicationsSection({ patterns, canWrite, today }: ApplicationsSectionProps) {
  const { readOnly } = useReadOnly();
  const { draft, applied, update, apply, clear, dirty, isFiltered, canClear } =
    useAppliedFilters<ApplicationDraft>(DEFAULT_DRAFT);
  const filters = useMemo(() => applicationFiltersFrom(applied), [applied]);
  const query = usePatternApplications(filters);
  const applications = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );
  const workers = useWorkerOptions();
  const [ending, setEnding] = useState<WorkerSchedulePattern | null>(null);

  const patternOptions: SelectOption[] = useMemo(
    () => [
      { value: ALL, label: t('schedules.patterns.applications.filters.allPatterns') },
      ...patterns.map((pattern) => ({ value: pattern.id, label: pattern.name })),
    ],
    [patterns],
  );
  const statusOptions: SelectOption[] = useMemo(
    () =>
      STATUSES.map((status) => ({
        value: status,
        label: t(`schedules.patterns.applications.filters.status.${status}`),
      })),
    [],
  );
  const workerOptions = useMemo(
    () => [
      { value: '', label: t('schedules.patterns.applications.filters.allWorkers') },
      ...workers.options,
    ],
    [workers.options],
  );

  return (
    <section className="flex flex-col gap-fm-3" aria-labelledby="pattern-applications-title">
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        <div>
          <h2
            id="pattern-applications-title"
            className="font-display text-fm-lg font-bold text-fm-text"
          >
            {t('schedules.patterns.applications.title')}
          </h2>
          <p className="text-fm-sm text-fm-text-secondary">
            {t('schedules.patterns.applications.description')}
          </p>
        </div>
        <RefreshButton query={query} pagesKey={patternApplicationsKey(filters)} />
      </div>

      <Card>
        <FilterForm onApply={apply} className="block">
          <fieldset className="flex flex-wrap items-end gap-fm-3">
            <legend className="sr-only">
              {t('schedules.patterns.applications.filters.legend')}
            </legend>
            <Field
              label={t('schedules.patterns.applications.filters.pattern')}
              className="w-[220px]"
            >
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.pattern_id}
                  onValueChange={(pattern_id) => update({ pattern_id })}
                  options={patternOptions}
                />
              )}
            </Field>
            <Field
              label={t('schedules.patterns.applications.filters.worker')}
              className="w-[220px]"
            >
              {(control) => (
                <Combobox
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.worker_id}
                  onValueChange={(worker_id) => update({ worker_id })}
                  options={workerOptions}
                  placeholder={t('schedules.patterns.applications.filters.allWorkers')}
                  searchPlaceholder={t('absences.form.workerSearch')}
                />
              )}
            </Field>
            <Field
              label={t('schedules.patterns.applications.filters.statusLabel')}
              className="w-[160px]"
            >
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.status}
                  onValueChange={(value) =>
                    update({ status: STATUSES.find((status) => status === value) ?? 'ACTIVE' })
                  }
                  options={statusOptions}
                />
              )}
            </Field>
            <FilterActions onSearch={apply} dirty={dirty} canClear={canClear} onClear={clear} />
          </fieldset>
        </FilterForm>
      </Card>

      {query.isPending ? <SkeletonLines lines={3} /> : null}

      {query.isError ? (
        <Card>
          <CardTitle>{t('schedules.patterns.applications.error')}</CardTitle>
          <CardDescription>{scheduleErrorMessage(query.error)}</CardDescription>
          <Button className="mt-fm-4" size="sm" onClick={() => void query.refetch()}>
            {t('common.buttons.retry')}
          </Button>
        </Card>
      ) : null}

      {query.isSuccess && applications.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title={
            isFiltered
              ? t('schedules.patterns.applications.noResults')
              : t('schedules.patterns.applications.empty')
          }
        />
      ) : null}

      {applications.length > 0 ? (
        <>
          <Table data-testid="pattern-applications-table">
            <TableCaption>{t('schedules.patterns.applications.title')}</TableCaption>
            <TableHead>
              <TableRow>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.worker')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.pattern')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.site')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.period')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.offset')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.generatedUntil')}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t('schedules.patterns.applications.columns.status')}
                </TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">
                    {t('schedules.patterns.applications.columns.actions')}
                  </span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => {
                const integration = application.source === 'INTEGRATION';
                const endable = canWrite && application.status === 'ACTIVE' && !integration;
                return (
                  <TableRow
                    key={application.id}
                    data-testid="pattern-application-row"
                    data-status={application.status}
                  >
                    <TableCell className="font-semibold">{application.worker.name}</TableCell>
                    <TableCell>{application.pattern.name}</TableCell>
                    <TableCell>{application.site.name}</TableCell>
                    <TableCell className="fm-tabular">
                      {application.end_day
                        ? t('schedules.patterns.applications.period', {
                            from: formatLocalDay(application.start_day),
                            to: formatLocalDay(application.end_day),
                          })
                        : t('schedules.patterns.applications.openPeriod', {
                            from: formatLocalDay(application.start_day),
                          })}
                    </TableCell>
                    <TableCell>
                      {t('schedules.patterns.applications.offset', {
                        n: application.cycle_offset + 1,
                      })}
                    </TableCell>
                    <TableCell className="fm-tabular">
                      {application.generated_until
                        ? formatLocalDay(application.generated_until)
                        : t('schedules.patterns.applications.notGenerated')}
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-col gap-fm-1">
                        {application.status === 'ACTIVE' ? (
                          <Badge variant="activa">
                            {t('schedules.patterns.applications.status.ACTIVE')}
                          </Badge>
                        ) : (
                          <Badge variant="inactiva">
                            {t('schedules.patterns.applications.status.ENDED')}
                          </Badge>
                        )}
                        {application.end_reason ? (
                          <span className="text-fm-xs text-fm-text-secondary">
                            {t(
                              `schedules.patterns.applications.endReason.${application.end_reason}`,
                            )}
                          </span>
                        ) : null}
                        {integration ? (
                          <span className="inline-flex items-center gap-fm-1 text-fm-xs text-fm-text-secondary">
                            <Lock size={12} aria-hidden="true" />
                            {t('schedules.source.INTEGRATION')}
                          </span>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell>
                      {endable ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="pattern-application-end"
                          aria-label={t('schedules.patterns.applications.endLabel', {
                            pattern: application.pattern.name,
                            worker: application.worker.name,
                          })}
                          onClick={() => setEnding(application)}
                          {...readOnlyProps(readOnly)}
                        >
                          {t('schedules.patterns.applications.end')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common.buttons.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {canWrite ? (
        <EndApplicationDialog application={ending} onClose={() => setEnding(null)} today={today} />
      ) : null}
    </section>
  );
}
